import type { ConfigOf } from "@p0security/shared/install/types";
import {
  Component,
  Element,
  InstallSpec,
  ItemComponent,
  Option,
} from "@p0security/shared/install/types";
import { pathSegmentIdValidator } from "@p0security/shared/install/validators";

import { classifyLoadBalancerAddress, isHttpUrl } from "./network.ts";

export const BUILTIN_SERVER_PATTERN = /^(a2a|p0)-([^/]+)$/;

const reservedPrefixMessage = (id: string) => {
  const builtin = id.match(BUILTIN_SERVER_PATTERN);
  return builtin ? `'${builtin[1]}-' is a reserved server prefix` : undefined;
};

const serverIdValidator = async (id: string) =>
  (await pathSegmentIdValidator(id)) ?? reservedPrefixMessage(id);

const LOAD_BALANCER_ADDRESS_HINT =
  "Enter a valid IPv4/IPv6 address or DNS hostname for your gateway's LoadBalancer.";

// The gateway identifier becomes the Helm release name and the name of the
// GatewayClass the chart creates, both of which Kubernetes requires to be
// lowercase RFC 1123 labels. 53 characters is the Helm release name limit.
const GATEWAY_ID_PATTERN = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/;
const GATEWAY_ID_MAX_LENGTH = 53;

export const GATEWAY_ID_HINT =
  "Use lowercase letters, digits, and hyphens only, starting and ending with " +
  `a letter or digit, up to ${GATEWAY_ID_MAX_LENGTH} characters.`;

// Factory so each Option's schema gets its own inferred type. Required for
// p0Hosted (P0 creates the DNS record from it); optional for selfHosted.
const loadBalancerIpElement = (opts: { optional?: true } = {}) =>
  Element({
    label: "LoadBalancer address",
    description:
      "The IP address or hostname your gateway's LoadBalancer was assigned; used to generate the correct DNS record instructions for your configured gateway URL.",
    type: "string",
    ...opts,
    validator: async (_id, field) =>
      classifyLoadBalancerAddress(field)
        ? undefined
        : LOAD_BALANCER_ADDRESS_HINT,
  });

const domainHostingOptions = {
  p0Hosted: Option({
    maturity: "ga" as const,
    label: "P0-hosted domain",
    description:
      "P0 hands out a ready-to-use hostname and creates its DNS record for you — nothing to manage yourself.",
    schema: {
      hostedDomain: Element({
        label: "Gateway hostname",
        description:
          "The hostname P0 assigned this gateway, computed from your organization and gateway id.",
        type: "generated",
        // Generated once from org slug + gateway id + a random suffix —
        // locked to "new" so a later `configure` call can't overwrite it.
        step: "new",
      }),
      loadBalancerIp: loadBalancerIpElement(),
    },
  }),
  selfHosted: Option({
    maturity: "ga" as const,
    label: "Self-hosted domain",
    description: "Bring your own domain and manage its DNS records yourself.",
    schema: {
      url: Element({
        label: "Agentic gateway URL",
        description: "Agentic gateway URL; your servers will be hosted here",
        type: "string",
        step: "new",
        validator: async (_id, field) =>
          isHttpUrl(field) ? undefined : "Gateway URL must be a web URL",
      }),
      "oauth-endpoint": Element({
        label: "OAuth server endpoint",
        description:
          "OAuth server endpoint; must be publicly accessible and host .well-known/jwks.json. " +
          "Defaults to the gateway URL — only set this if your OAuth/JWKS endpoint runs on a different host than the gateway itself.",
        type: "string",
        step: "new",
        optional: true,
        validator: async (_id, field) =>
          !field || isHttpUrl(field)
            ? undefined
            : "Gateway OAuth endpoint must be a web URL",
      }),
      loadBalancerIp: loadBalancerIpElement({ optional: true }),
    },
  }),
};

const domainHosting = Element({
  label: "Domain hosting",
  description:
    "How your gateway's public hostname and DNS records are managed.",
  type: "select",
  default: "p0Hosted",
  step: "new",
  options: domainHostingOptions,
});

const logProvider = ItemComponent({
  label: "Log provider",
  description:
    "A source of tool-call activity logs, shared by one or more gateways",
  type: "string",
  maturity: "ga",
  schema: {
    provider: Element({
      label: "Provider",
      type: "select",
      default: "gcp",
      options: {
        gcp: Option({
          maturity: "beta" as const,
          label: "Google Cloud Logging",
          schema: {
            projectId: Element({
              label: "GCP log project ID",
              description:
                "GCP project ID where Cloud Logging entries actually land. " +
                "This is the project holding the log bucket, which may not be the same project a gateway itself runs in (e.g. if logs are routed to a centralized logging project) — different buckets within this same project are already covered.",
              type: "string",
            }),
          },
        }),
        victorialogs: Option({
          maturity: "ga" as const,
          label: "VictoriaLogs (via gateway)",
          description:
            "Activity is read through the gateway's own /manage/logs/query endpoint, which proxies a LogsQL query to the VictoriaLogs instance deployed alongside it. Needs no configuration here: the query is authenticated with the same service account identity P0 already uses to manage the gateway.",
          // The gateway itself is the query target, so this option carries no
          // settings of its own.
          schema: {},
        }),
      },
    }),
  },
});

const gateway = ItemComponent({
  label: "Gateway",
  description: "Control access to resources over MCP and A2A.",
  type: "string",
  maturity: "ga",
  validator: async (id) =>
    id.length <= GATEWAY_ID_MAX_LENGTH && GATEWAY_ID_PATTERN.test(id)
      ? undefined
      : GATEWAY_ID_HINT,
  schema: {
    domainHosting,
    "log-provider": Element({
      label: "Log provider",
      description:
        "Where this gateway's tool-call activity logs are stored, so P0 can show its MCP tool-call activity",
      type: "dynamic",
      optional: true,
    }),
    // TODO: We shouldn't have legacy cruft when we have no prod deploys
    "log-project-id": Element({
      label: "GCP log project ID",
      description:
        "Superseded by `log-provider`. Gateways configured before logging sources became their own component hold their GCP log project here; it is read only when no log provider is linked, and is carried through reconfiguration so that activity keeps resolving.",
      type: "hidden",
      optional: true,
    }),
    "installed-version": Element({
      label: "Installed gateway version",
      description:
        "Release version the gateway reported when it was verified. Absent for gateways too old to report one.",
      type: "hidden",
      hidden: true,
      optional: true,
    }),
    serviceAccountEmail: Element({
      label: "GCP service account email",
      description:
        "Email address of the service account identity that P0 uses to communicate with your gateway",
      type: "hidden",
    }),
    // Bootstrap key for the mandatory braekhus tunnel: extracted via
    // `kubectl exec` (app2 can't reach a private gateway directly) and
    // pasted during `configure`. Optional — older gateways have none yet
    // and keep using the direct JWKS fetch. Mirrors k8s's `connectivity.publicJwk`.
    braekhusPublicJwk: Element({
      label: "Management tunnel public key",
      description:
        "JWK the gateway's outbound tunnel uses to authenticate to P0. " +
        "Extract it from the gateway and paste it here.",
      type: "string",
      multiline: true,
      optional: true,
      validator: async (_id, field) => {
        if (!field) return undefined;
        let parsed: unknown;
        try {
          parsed = JSON.parse(field);
        } catch {
          return "Provide the public key as JSON (the contents of jwk.public.json)";
        }
        // EC/P-384 only, no `d` (private key) — braekhus verifies with a
        // fixed ES384 alg and no allowlist, so a weaker key type is exploitable.
        if (
          typeof parsed !== "object" ||
          parsed === null ||
          "d" in parsed ||
          !("kty" in parsed) ||
          parsed.kty !== "EC" ||
          !("crv" in parsed) ||
          parsed.crv !== "P-384"
        ) {
          return "Provide the public EC (P-384) key (not the private key) as a JWK object";
        }
      },
    }),
  },
});

const p0ServerDefinition = Option({
  maturity: "ga" as const,
  label: "P0",
  description: "Server defined by P0",
  schema: {
    id: {
      label: "Identifier",
      description: "Pre-defined server identifier",
      type: "dynamic",
    },
    instance: Element({
      label: "Instance",
      description:
        "The value that the server puts in its URL, such as a tenant identifier",
      type: "string",
      optional: true,
      // The URL and its OAuth tokens belong to one instance
      step: "new",
    }),
  },
});

const customServerDefinition = Option({
  maturity: "ga" as const,
  label: "Custom",
  description: "Server defined by your organization",
  schema: {
    hosting: Element({
      label: "Server type",
      description:
        "Whether this server proxies to an external server or hosts a containerized server",
      type: "select",
      default: "external",
      options: {
        container: Component({
          label: "Container",
          description: "This server runs a Docker container to host tools",
          maturity: "preview",
          schema: {
            label: Element({
              label: "Label",
              description: "Human-friendly label for this server",
              type: "string",
            }),
            entrypoint: Element({
              label: "Docker entrypoint",
              description:
                "Container run entrypoint; use Liquid syntax to template request parameters and credentials",
              type: "string",
              multiline: true,
            }),
            image: Element({
              label: "Docker image",
              description: "Image that hosts the MCP server",
              type: "string",
            }),
            location: Element({
              label: "Location field name",
              description:
                'Name of the field agents must supply to route calls to this server (e.g. "project", "account")',
              type: "string",
            }),
            locationDescription: Element({
              label: "Location field description",
              description:
                "Agent-consumable description of the value to pass for the location field",
              type: "string",
            }),
          },
        }),
        external: Component({
          label: "External",
          description:
            "This server proxies requests to an MCP server hosted outside the gateway, reachable at a fixed URL",
          maturity: "ga",
          schema: {
            url: Element({
              label: "Server URL",
              description: "URL of the externally hosted MCP server",
              type: "string",
            }),
            label: Element({
              label: "Label",
              description: "Human-friendly label for this server",
              type: "string",
            }),
          },
        }),
      },
    }),
    control: Element({
      label: "Access control method",
      description: "How access to this MCP resource is controlled",
      type: "select",
      options: {
        annotation: Option({
          label: "Annotation",
          description:
            "Agents request access to MCP annotations (readOnly, destructive, or neither)",
          maturity: "ga",
          schema: {},
        }),
        tool: Option({
          label: "Tool",
          description: "Agents request access to individual tools",
          maturity: "ga",
          schema: {},
        }),
        native: Option({
          label: "Native",
          description: "Access is controlled in the upstream environment's IAM",
          maturity: "ga",
          schema: {
            prompt: Element({
              label: "Agent prompt",
              description:
                "Describe the argument list an agent should pass to request access",
              type: "string",
              multiline: true,
            }),
            resource: Element({
              label: "Resource",
              description: "An installed access-management resource",
              type: "dynamic",
            }),
            prefixArgs: Element({
              label: "Access command prefix",
              description:
                "A prefix added to request arguments when the agent request access; e.g. `role MyAccessRole`",
              type: "string",
            }),
          },
        }),
      },
      default: "annotation" as const,
    }),
  },
});

const credentialSource = Element({
  label: "Credential source",
  type: "select",
  options: {
    aws: Option({
      maturity: "ga" as const,
      label: "AWS IAM federation",
      description: "Via an installed IAM federation provider",
      schema: {
        provider: Element({
          label: "Federation provider",
          type: "dynamic",
        }),
      },
    }),
    gcp: Option({
      maturity: "ga" as const,
      label: "GCP WIF federation",
      description: "Via an installed WIF provider",
      schema: {
        provider: Element({
          label: "Federation provider",
          type: "dynamic",
        }),
      },
    }),
    native: Option({
      maturity: "ga" as const,
      label: "Native credentials",
      description: "Upstream accepts gateway OAuth tokens",
      schema: {
        claims: Element({
          label: "Claims",
          description:
            "Claims JSON; use Liquid syntax to template request parameters",
          type: "string",
          multiline: true,
          optional: true,
          placeholder: '{ "scopes": {{ request.permission.role | json }} }',
        }),
      },
    }),
    oauth: Option({
      maturity: "ga" as const,
      label: "OAuth",
      description: "Request OAuth credentials from an upstream MCP server",
      schema: {
        grant: Element({
          label: "Grant type",
          description: "The OAuth grant used to obtain the credential",
          type: "select",
          default: "authorization_code",
          options: {
            authorization_code: Option({
              maturity: "ga" as const,
              label: "Authorization code",
              description: "OAuth 2.0 authorization-code grant",
              schema: {
                scope: Element({
                  label: "Scopes",
                  description:
                    "Optional comma-delimited list of scopes to request",
                  type: "string",
                  optional: true,
                }),
              },
            }),
          },
        }),
      },
    }),
    vault: Option({
      maturity: "preview" as const,
      label: "Vaulted credential",
      description:
        "P0 provisions the credential into a secret manager, and the grant says which",
      schema: {},
    }),
    generic: Option({
      maturity: "ga" as const,
      label: "Token exchange (RFC 8693)",
      description:
        "The gateway exchanges its own token at the token endpoint that the server definition names",
      schema: {},
    }),
    impersonation: Option({
      maturity: "preview" as const,
      label: "Service account impersonation",
      description:
        "P0 provisions an identity for each grant, and the gateway acts as it",
      schema: {},
    }),
  },
  default: "native",
});

const serverDefinition = Element({
  label: "Definition",
  type: "select",
  description:
    "Whether this server is predefined by P0, or a custom server definition",
  default: "p0",
  options: {
    p0: p0ServerDefinition,
    custom: customServerDefinition,
  },
});

const gatewaySelect = Element({
  label: "Gateway",
  type: "dynamic",
  description: "The gateway that hosts this server",
  step: "new",
});

const mcpServer = ItemComponent({
  label: "MCP server",
  description: "Provide agents access to your resources",
  type: "string",
  maturity: "ga",
  // The ID is a path segment of the gateway's URLs
  validator: serverIdValidator,
  schema: {
    gateway: gatewaySelect,
    definition: serverDefinition,
    credential: credentialSource,
  },
});

const a2aBridge = ItemComponent({
  label: "A2A bridge",
  description: "Apply policy to agent-to-agent calls",
  type: "string",
  maturity: "ga",
  // The ID is a path segment of the gateway's URLs
  validator: serverIdValidator,
  schema: {
    gateway: gatewaySelect,
    baseUrl: Element({
      label: "Agent host",
      type: "string",
      description: "Base URL at which upstream agents are hosted",
    }),
  },
});

export const AgenticComponents = InstallSpec({
  "a2a-bridge": a2aBridge,
  gateway,
  "log-provider": logProvider,
  server: mcpServer,
});

export type AgenticIntegration = ConfigOf<typeof AgenticComponents>;

export type AgenticMcpServer = AgenticIntegration["server"][string];
